import axiosClient from './axiosClient';
import type { OpportunityListItem, Opportunity, OpportunityCreate, OpportunityUpdate } from '@/types/opportunity';

// AI-extracted intake fields from an uploaded RFO/RFP document.
export interface OpportunityIntakeExtraction {
  title?: string;
  agencyName?: string;
  description?: string;
  sourcePortal?: string;
  sourceUrl?: string;
  rfoNumber?: string;
  submissionMethod?: string;
  dueDate?: string;
  qaDeadline?: string;
  keyContacts?: string;
  extracted: boolean;
  note?: string;
}

// Spring Boot Page<T> response shape
export interface SpringPageResponse<T> {
  content: T[];
  pageable: {
    pageNumber: number;
    pageSize: number;
  };
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export const opportunityApi = {
  getAll: async (params?: {
    page?: number;
    size?: number;
    status?: string;
    search?: string;
    excludeStatuses?: string[];
  }): Promise<SpringPageResponse<OpportunityListItem>> => {
    const response = await axiosClient.get('/opportunities', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Opportunity> => {
    const response = await axiosClient.get(`/opportunities/${id}`);
    return response.data;
  },

  create: async (data: OpportunityCreate): Promise<Opportunity> => {
    const response = await axiosClient.post('/opportunities', data);
    return response.data;
  },

  // Upload an RFO/RFP document and get AI-extracted intake fields back.
  extractIntake: async (file: File): Promise<OpportunityIntakeExtraction> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosClient.post('/opportunities/extract-intake', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, data: OpportunityUpdate): Promise<Opportunity> => {
    const response = await axiosClient.put(`/opportunities/${id}`, data);
    return response.data;
  },

  updateIntakeDetails: async (
    id: string,
    data: {
      agencyName?: string;
      rfoNumber?: string;
      submissionMethod?: string;
      dueDate?: string;
      dueTime?: string;
      discoveryDate?: string;
      sourcePortal?: string;
      externalRef?: string;
      mandatoryRequirements?: string;
      keyContacts?: string;
      qaDeadline?: string;
      awardDate?: string;
      awardTime?: string;
      extendedSubmissionDate?: string;
    },
  ): Promise<Opportunity> => {
    const response = await axiosClient.patch(`/opportunities/${id}/intake-details`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosClient.delete(`/opportunities/${id}`);
  },

  completeIntake: async (id: string): Promise<Opportunity> => {
    const response = await axiosClient.post(`/opportunities/${id}/intake`);
    return response.data;
  },

  getExtractedRoles: async (id: string): Promise<ExtractedRole[]> => {
    const response = await axiosClient.get(`/opportunities/${id}/extracted-roles`);
    return response.data;
  },

  getAssignedCandidates: async (id: string): Promise<AssignedCandidate[]> => {
    const response = await axiosClient.get(`/opportunities/${id}/assigned-candidates`);
    return response.data;
  },
};

export interface ExtractedRole {
  roleTitle: string;
  count: number;
  classification?: string;
  fullTimeOrPartTime?: string;
  mandatoryQualifications?: string;
  desirableQualifications?: string;
  responsibilities?: string;
  sourceSection?: string;
}

export interface AssignedCandidate {
  candidateId: string;
  candidateName: string;
  assignmentRole?: string;
  stage?: string;
  assignedAt: string;
  notes?: string;
}
